import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Webhook, MessageSquare, CircleDollarSign, Trophy } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

export default function AdminSettings() {
  const [webhook, setWebhook] = useState("");
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPw, setChangingPw] = useState(false);
  const [ownerPhone, setOwnerPhone] = useState("");
  const [ownerSmsLeads, setOwnerSmsLeads] = useState(false);
  const [savingOwner, setSavingOwner] = useState(false);
  const [fee, setFee] = useState("20");
  const [guarantee, setGuarantee] = useState("30");
  const [baseStripe, setBaseStripe] = useState("0");
  const [baseClients, setBaseClients] = useState("0");
  const [savingBaseline, setSavingBaseline] = useState(false);
  const [savingFee, setSavingFee] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setWebhook(r.data.discordWebhook || "")).catch(() => {});
    api.get("/company").then((r) => {
      setOwnerPhone(r.data.owner_phone || "");
      setOwnerSmsLeads(!!r.data.owner_sms_leads);
      setFee(String(r.data.creator_fee_usd ?? 20));
      setGuarantee(String(r.data.creator_fee_guarantee_aud ?? 30));
      setBaseStripe(String((r.data.stats_baseline || {}).stripe_processed_aud ?? 0));
      setBaseClients(String((r.data.stats_baseline || {}).clients_served ?? 0));
    }).catch(() => {});
  }, []);

  const saveOwnerAlerts = async (e) => {
    e.preventDefault();
    setSavingOwner(true);
    try {
      await api.put("/company", { owner_phone: ownerPhone.trim(), owner_sms_leads: ownerSmsLeads });
      toast.success("Owner SMS alerts saved");
    } catch {
      toast.error("Failed to save owner alerts");
    } finally {
      setSavingOwner(false);
    }
  };

  const saveWebhook = async (e) => {
    e.preventDefault();
    setSavingWebhook(true);
    try {
      await api.put("/settings", { discordWebhook: webhook.trim() });
      toast.success("Discord webhook saved");
    } catch {
      toast.error("Failed to save webhook");
    } finally {
      setSavingWebhook(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", { currentPassword, newPassword });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <section data-testid="admin-settings-section" className="border border-white/10 bg-black/40">
      <div className="border-b border-white/10 px-6 py-4">
        <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100">Settings</h2>
      </div>
      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={saveWebhook} className="border border-white/10 p-4 bg-[#050505]/50 flex flex-col gap-3">
          <div className="flex items-center gap-2 mb-1">
            <Webhook className="w-4 h-4 text-nc-blue" />
            <h3 className="font-sans font-semibold uppercase tracking-widest text-sm text-zinc-100">Discord Notifications</h3>
          </div>
          <p className="font-mono text-[11px] text-zinc-500 leading-relaxed">
            Paste a Discord webhook URL and every new devlog transmission gets posted to your server automatically.
            In Discord: Channel Settings → Integrations → Webhooks → New Webhook → Copy Webhook URL.
          </p>
          <div>
            <label className={labelCls}>Webhook URL</label>
            <input
              data-testid="admin-webhook-input"
              className={inputCls}
              value={webhook}
              onChange={(e) => setWebhook(e.target.value)}
              placeholder="https://discord.com/api/webhooks/..."
            />
          </div>
          <button
            data-testid="admin-webhook-save-button"
            type="submit"
            disabled={savingWebhook}
            className="self-start font-sans font-semibold text-sm uppercase tracking-[0.15em] bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-50 text-white px-5 py-2 transition-colors duration-300"
          >
            {savingWebhook ? "Saving..." : "Save Webhook"}
          </button>
        </form>

        <form onSubmit={changePassword} className="border border-white/10 p-4 bg-[#050505]/50 flex flex-col gap-3">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="w-4 h-4 text-nc-orange" />
            <h3 className="font-sans font-semibold uppercase tracking-widest text-sm text-zinc-100">Change Password</h3>
          </div>
          <div>
            <label className={labelCls}>Current Password</label>
            <input
              data-testid="admin-current-password-input"
              type="password"
              required
              className={inputCls}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>New Password (min 8 characters)</label>
            <input
              data-testid="admin-new-password-input"
              type="password"
              required
              minLength={8}
              className={inputCls}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Confirm New Password</label>
            <input
              data-testid="admin-confirm-password-input"
              type="password"
              required
              minLength={8}
              className={inputCls}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          <button
            data-testid="admin-change-password-button"
            type="submit"
            disabled={changingPw}
            className="self-start font-sans font-semibold text-sm uppercase tracking-[0.15em] bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-50 text-white px-5 py-2 transition-colors duration-300"
          >
            {changingPw ? "Updating..." : "Change Password"}
          </button>
        </form>

        <form onSubmit={saveOwnerAlerts} className="border border-white/10 p-4 bg-[#050505]/50 flex flex-col gap-3">
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare className="w-4 h-4 text-nc-green" />
            <h3 className="font-sans font-semibold uppercase tracking-widest text-sm text-zinc-100">Owner SMS Alerts</h3>
          </div>
          <p className="font-mono text-[11px] text-zinc-500 leading-relaxed">
            Get a text message the moment any new lead lands in the sales pipeline. SMS sends once Twilio credentials are configured on the server — until then alerts are skipped silently (email alerts keep working).
          </p>
          <div>
            <label className={labelCls}>Your phone (international format)</label>
            <input
              data-testid="admin-owner-phone-input"
              className={inputCls}
              value={ownerPhone}
              onChange={(e) => setOwnerPhone(e.target.value)}
              placeholder="+61412345678"
              maxLength={20}
            />
          </div>
          <label className="flex items-center gap-3 font-mono text-[11px] text-zinc-300 cursor-pointer">
            <input
              type="checkbox"
              data-testid="admin-owner-sms-toggle"
              checked={ownerSmsLeads}
              onChange={(e) => setOwnerSmsLeads(e.target.checked)}
              className="w-4 h-4 accent-[#00994d]"
            />
            SMS me on every new lead
          </label>
          <button
            data-testid="admin-owner-alerts-save-button"
            type="submit"
            disabled={savingOwner}
            className="self-start font-sans font-semibold text-sm uppercase tracking-[0.15em] bg-nc-green hover:bg-nc-green/80 disabled:opacity-50 text-black px-5 py-2 transition-colors duration-300"
          >
            {savingOwner ? "Saving..." : "Save SMS Alerts"}
          </button>
        </form>

        <form onSubmit={async (e) => {
          e.preventDefault();
          setSavingFee(true);
          try {
            await api.put("/company", { creator_fee_usd: parseFloat(fee) || 0, creator_fee_guarantee_aud: parseFloat(guarantee) || 0 });
            toast.success("Creator program pricing updated");
          } catch {
            toast.error("Failed to save fee");
          } finally {
            setSavingFee(false);
          }
        }} className="border border-white/10 p-4 bg-[#050505]/50 flex flex-col gap-3">
          <div className="flex items-center gap-2 mb-1">
            <CircleDollarSign className="w-4 h-4 text-nc-blue" />
            <h3 className="font-sans font-semibold uppercase tracking-widest text-sm text-zinc-100">Creator Program Fee</h3>
          </div>
          <p className="font-mono text-[11px] text-zinc-500 leading-relaxed">
            One-time activation price for approved creators (buys them the Local Forge Kit). Stripe checkout, cards + crypto.
          </p>
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm text-zinc-400">USD</span>
            <input
              data-testid="admin-creator-fee-input"
              type="number"
              min="1"
              step="1"
              className={inputCls}
              value={fee}
              onChange={(e) => setFee(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm text-zinc-400">AUD</span>
            <input
              data-testid="admin-creator-guarantee-input"
              type="number"
              min="0"
              step="1"
              className={inputCls}
              value={guarantee}
              onChange={(e) => setGuarantee(e.target.value)}
            />
            <span className="font-mono text-[10px] text-zinc-600">earn-back first-bounty floor</span>
          </div>
          <button
            data-testid="admin-creator-fee-save"
            type="submit"
            disabled={savingFee}
            className="self-start font-sans font-semibold text-sm uppercase tracking-[0.15em] bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-50 text-white px-5 py-2 transition-colors duration-300"
          >
            {savingFee ? "Saving..." : "Save Fee"}
          </button>
        </form>

        <form onSubmit={async (e) => {
          e.preventDefault();
          setSavingBaseline(true);
          try {
            await api.put("/company", { stats_baseline: { stripe_processed_aud: parseFloat(baseStripe) || 0, clients_served: parseInt(baseClients) || 0 } });
            toast.success("Track record updated — proof numbers now show your real history");
          } catch {
            toast.error("Failed to save track record");
          } finally {
            setSavingBaseline(false);
          }
        }} className="border border-white/10 p-4 bg-[#050505]/50 flex flex-col gap-3">
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="w-4 h-4 text-nc-orange" />
            <h3 className="font-sans font-semibold uppercase tracking-widest text-sm text-zinc-100">Track Record — Public Proof Numbers</h3>
          </div>
          <p className="font-mono text-[11px] text-zinc-500 leading-relaxed">
            Your verified pre-platform history (from your Stripe dashboard). Added on top of live platform numbers everywhere the public proof band appears. Only enter figures you can back up — this is your real company record.
          </p>
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm text-zinc-400 shrink-0">AUD</span>
            <input data-testid="admin-baseline-stripe" type="number" min="0" step="0.01" className={inputCls} value={baseStripe} onChange={(e) => setBaseStripe(e.target.value)} />
            <span className="font-mono text-[10px] text-zinc-600 shrink-0">historical Stripe volume</span>
          </div>
          <div className="flex items-center gap-3">
            <input data-testid="admin-baseline-clients" type="number" min="0" step="1" className={inputCls} value={baseClients} onChange={(e) => setBaseClients(e.target.value)} />
            <span className="font-mono text-[10px] text-zinc-600 shrink-0">clients served</span>
          </div>
          <button data-testid="admin-baseline-save" type="submit" disabled={savingBaseline} className="self-start font-sans font-semibold text-sm uppercase tracking-[0.15em] bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-50 text-black px-5 py-2 transition-colors duration-300">
            {savingBaseline ? "Saving..." : "Save Track Record"}
          </button>
        </form>
      </div>
    </section>
  );
}
